import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { stockAdjustmentPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { catalogApi } from '../catalog/catalogApi';
import {
  ADJUSTMENT_KINDS,
  ADJUSTMENT_STATUSES,
  adjustmentKindMeta,
  adjustmentStatusMeta,
  adjustmentsApi,
} from './adjustmentsApi';
import '../requisitions/requisitions.css';

const PAGE_SIZE = 25;

export default function AdjustmentsPage() {
  const navigate = useNavigate();
  const t = useT();
  const { activePropertyId, hasPermission } = useProperty();
  const canAdjust = hasPermission('inventory.adjust');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    let active = true;
    catalogApi.listLocations(activePropertyId).then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: ADJUSTMENT_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'kinds', label: t('Kind'), type: 'multiselect', options: ADJUSTMENT_KINDS.map((k) => ({ value: k.value, label: t(k.label) })) },
    { id: 'locationIds', label: t('Location'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'adjusted', label: t('Adjustment date'), type: 'daterange', fromParam: 'adjustedFrom', toParam: 'adjustedTo' },
  ], [locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adjustmentsApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load adjustments.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const locationName = (lid) => locations.find((l) => l.id === lid)?.name || '—';
  const open = (row) => navigate(stockAdjustmentPath(row));

  const columns = [
    { key: 'number', header: t('Adjustment'), width: 130, render: (a) => <span style={{ fontFamily: 'var(--font-mono)' }}>{a.number || t('Draft')}</span> },
    {
      key: 'kind', header: t('Kind'), width: 160,
      render: (a) => {
        const m = adjustmentKindMeta(a.kind);
        return <Badge tone={m.tone}>{t(m.label)}</Badge>;
      },
    },
    { key: 'location', header: t('Location'), width: 180, render: (a) => locationName(a.adjustmentLocationId) },
    {
      key: 'status', header: t('Status'), width: 110,
      render: (a) => {
        const m = adjustmentStatusMeta(a.status);
        return <Badge tone={m.tone} dot>{t(m.label)}</Badge>;
      },
    },
    { key: 'date', header: t('Date'), width: 120, render: (a) => (a.adjustmentDate ? new Date(a.adjustmentDate).toLocaleDateString() : '—') },
    { key: 'lines', header: t('Items'), width: 80, align: 'right', render: (a) => a.lineCount },
    { key: 'total', header: t('Value'), width: 140, align: 'right', render: (a) => money(a.total) },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Adjustments')}
        description={t('Write off waste and spoilage, issue stock for operational consumption, or record signed corrections — every change hits the stock ledger with a reason.')}
        icon={<Icon.TrendingDown size={22} />}
        actions={canAdjust ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/adjustments/new')}>
            {t('New adjustment')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by adjustment number, item or notes…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load adjustments")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(a) => a.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.TrendingDown size={28} />}
                title={hasFilters ? t('No matching adjustments') : t('No adjustments yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Record waste, spoilage, consumption or corrections so the stock ledger always matches reality.')}
                action={canAdjust && !hasFilters ? <Button variant="secondary" onClick={() => navigate('/adjustments/new')}>{t('New adjustment')}</Button> : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
